<template>
  <HeaderContent title="SPJ (Surat Perintah Jalan)" />
  <ActionContent
    ctaText="SPJ"
    :filterDate="true"
    v-model:startDate="startDate"
    v-model:endDate="endDate"
    :filterModal="false"
    @action="gotoAdd"
    @search-query="searchData"
  />

  <DataTable
    :data="spjs"
    :header="fields"
    :custom-actions="customActions"
    :is-busy="loading"
  />
</template>

<script setup>
import { ref, onMounted, inject } from "vue";
import axios from "axios";
import HeaderContent from "@/components/HeaderContent.vue";
import ActionContent from "@/components/ActionContent.vue";
import DataTable from "@/components/DataTable.vue";
import { useToast } from "vue-toastification";
import { useAuthStore } from "@/store/auth";
import { useLoading } from "vue-loading-overlay";
import { useRouter } from "vue-router";
// import dayjs from "dayjs";

const toast = useToast();
const auth = useAuthStore();
const $loading = useLoading();
const swal = inject("$swal");
const router = useRouter();

// State
const spjs = ref([]);
const loading = ref(false);
const startDate = ref("");
const endDate = ref("");

// Table fields
const fields = [
  { key: "no", label: "No", sortable: false },
  { key: "spj_no", label: "SPJ No", sortable: true },
  { key: "offer.offer_no", label: "Invoice No", sortable: true },
  { key: "offer.name", label: "Offer Name", sortable: true },
  {
    key: "guest",
    label: "Guest Name",
    sortable: true,
    formatter: (value) =>
      value
        ? value.alias
          ? `${value.alias}. ${value.fullname}`
          : value.fullname
        : "-",
  },
  { key: "staff_name", label: "Driver/Guide", sortable: true },
  { key: "start", label: "Departure", sortable: true },
  { key: "end", label: "Return", sortable: true },
  { key: "payment_status", label: "Payment Status", sortable: true },
  { key: "status_offer", label: "Status", sortable: false },
  {
    key: "accurate_status",
    label: "Accurate",
    sortable: false,
    thClass: "text-center",
    tdClass: "text-center",
  },
  { key: "action", label: "Action", sortable: false },
];

const hasRole = (allowedRoles) => {
  const userRoles = auth._roles;
  if (!userRoles) return false;
  const roles = Array.isArray(userRoles) ? userRoles : [userRoles];
  return roles.some((role) => allowedRoles.includes(role));
};

const customActions = ref([
  {
    label: (item) => (item.accurate_id ? "Update Accurate" : "Sync Accurate"),
    variant: "info",
    icon: "fas fa-sync",
    action: (item) => {
      syncToAccurate(item.uuid);
    },
    showIf: (item) => {
      // Show for Approved or Completed statuses
      return (
        !item.accurate_synced &&
        (item.status_offer === "Approved" || item.status_offer === "Completed")
      );
    },
  },
  {
    label: "Detail",
    variant: "primary",
    icon: "fas fa-info-circle",
    showIf: () => {
      // Everyone can see detail? Or restricted? User didn't say.
      // Usually Detail is safe.
      return true;
    },
    action: (item) => {
      router.push({ name: "spj-detail", params: { uuid: item.uuid } });
    },
  },
  {
    label: "Edit",
    variant: "success",
    icon: "fas fa-edit",
    showIf: (item) => {
      if (hasRole(["Super Admin", "Admin Office"])) return true;
      if (hasRole(["Manager Driver"])) {
        const status = item.status?.toLowerCase();
        return status === "pending" || status === "rejected";
      }
      return false;
    },
    action: (item) => {
      router.push({ name: "spj-edit", params: { uuid: item.uuid } });
    },
  },
  {
    label: "Delete",
    variant: "danger",
    icon: "fas fa-trash",
    showIf: (item) => {
      if (hasRole(["Super Admin", "Admin Office"])) return true;
      if (hasRole(["Manager Driver"])) {
        const status = item.status?.toLowerCase();
        return status === "pending" || status === "rejected";
      }
      return false;
    },
    action: (item) => {
      swal({
        title: "Confirm",
        text: `Are you sure to delete SPJ "${item.spj_no}"?`,
        showCancelButton: true,
        confirmButtonText: "Yes, delete!",
        cancelButtonText: "Cancel",
        customClass: {
          popup: "rounded",
          confirmButton: "btn btn-danger me-2",
          cancelButton: "btn btn-secondary",
        },
        buttonsStyling: false,
      }).then((confirm) => {
        if (confirm.value) {
          deleteSpj(item.uuid);
        }
      });
    },
  },
]);

// Methods
const searchData = async (search) => {
  await fetchSpjs(search);
};

const gotoAdd = () => {
  router.push({ name: "spj-add" });
};

const fetchSpjs = async (search = "") => {
  const loader = $loading.show({
    color: "#489464",
    loader: "spinner",
  });

  loading.value = true;
  try {
    const params = {
      search: search,
      start_date: startDate.value,
      end_date: endDate.value,
    };

    const response = await axios.get(`${process.env.VUE_APP_API_URL}/spj`, {
      headers: { Authorization: `Bearer ${auth._token}` },
      params,
    });

    if (response.data.status) {
      spjs.value = response.data.data.map((i) => {
        return {
          ...i,
          status_offer: i.status,
          accurate_status: i.accurate_synced ? "Synced" : (i.accurate_id ? "Need Re-sync" : "Pending"),
        };
      });
    }
  } finally {
    loader.hide();
    loading.value = false;
  }
};

const deleteSpj = async (uuid) => {
  const loader = $loading.show({
    color: "#009689",
    loader: "bars",
    zIndex: 999999,
  });

  try {
    const response = await axios.delete(
      `${process.env.VUE_APP_API_URL}/spj/delete/${uuid}`,
      {
        headers: { Authorization: `Bearer ${auth._token}` },
      },
    );

    if (response.data.status) {
      toast.success("SPJ deleted successfully");
      fetchSpjs();
    }
  } finally {
    loader.hide();
  }
};

const syncToAccurate = async (uuid) => {
  const loader = $loading.show({
    color: "#489464",
    loader: "spinner",
  });
  try {
    const response = await axios.post(
      `${process.env.VUE_APP_API_URL}/spj/sync/${uuid}`,
      {},
      {
        headers: {
          "Content-type": "application/json",
          Authorization: "Bearer " + auth._token,
        },
      },
    );

    const res = response.data;

    if (res.status) {
      toast.success("SPJ successfully synced to Accurate!");
      fetchSpjs();
    }
  } finally {
    loader.hide();
  }
};

import { watch } from "vue";

watch([startDate, endDate], () => {
  fetchSpjs();
});

// Lifecycle
onMounted(() => {
  fetchSpjs();
});
</script>
